import { createSupabaseAdminServerClient } from "#/lib/supabase/supabase-server";
import { Database } from "#/types/supabase";
import { isBrowser, isDevEnvironment } from "#/utils/environment";
import { User } from "@supabase/gotrue-js";
import { SupabaseClient } from "@supabase/supabase-js";
import { NextRequest } from "next/server";
import { IncomingHttpHeaders } from "http";
import { verifyAccessTokenForUserId } from "#/utils/jwt";

export const getCookieKey = (): `sb-${string}-auth-token` =>
  !isDevEnvironment() ? `sb-${process.env.NEXT_PUBLIC_SUPABASE_SLUG}-auth-token` : "sb-localhost-auth-token";

export const deserializeSupabaseAuthCookieValue = (
  cookieValue: string
): Record<string, string> => {
  let supabaseCookie: string;
  if (cookieValue.startsWith("base64-")) {
    supabaseCookie = Buffer.from(cookieValue.replace(/^base64-/, ""), "base64url").toString("utf-8");
  } else {
    supabaseCookie = cookieValue;
  }
  try {
    return JSON.parse(supabaseCookie);
  } catch (err) {
    console.error(err);
    throw err;
  }
};
export const getBrowserUserCookie = ({
  req,
  edgeRequest
}: GetBrowserUser): Record<string, string> | null => {
  // let supabaseCookie: string;
  let cookies: Record<string, string>;
  if (req) {
    if (Object.keys(req?.cookies).length === 0) {
      return null;
    }
    cookies = req.cookies;
  } else if (edgeRequest) {
    if (edgeRequest?.cookies.size === 0) {
      return null;
    }
    cookies = Object.assign({}, ...edgeRequest?.cookies.getAll().map(({ name, value }) => ({
      [name]: value
    })));
  } else {

    throw new Error("No request");
  }
  const cookieKey = getCookieKey();
  const cookieParts: { name: string, value: string }[] = [];
  if (cookies[cookieKey]) {
    cookieParts.push({ name: cookieKey, value: cookies[cookieKey] });
  } else {
    for (const [ key, value ] of Object.entries(cookies)) {
      if (key.startsWith(cookieKey)) {
        cookieParts.push({ name: key, value });
      }
    }
  }
  if (!cookieParts.length) {
    return null;
  }
  let supabaseCookie = cookieParts.sort((a, b) => a.name.localeCompare(b.name))
    .map(({ value }) => value).join("").trim();
  if (!supabaseCookie) {
    return null;
  }
  return deserializeSupabaseAuthCookieValue(supabaseCookie);
};

type GetBrowserUser =
  | {
    req: {
      headers?: IncomingHttpHeaders,
      cookies: Record<string, any>
    },
    res?: any,
    edgeRequest?: never
  } | {
    edgeRequest: NextRequest,
    req?: never,
    res?: never
  };

export const getBrowserJWT = async (
  args: GetBrowserUser
): Promise<string | null> => {
  if (isBrowser()) {
    throw new Error("Not allowed");
  }
  const supabaseCookie = getBrowserUserCookie(args);
  if (supabaseCookie) {
    return supabaseCookie?.access_token ?? null;
  }
  if (args?.req?.headers && args.req.headers.authorization) {
    const authHeader = args.req.headers.authorization;
    const token = authHeader.split(" ")?.[1];
    if (!token) {
      return null;
    }
    const { error: verifyError } = await verifyAccessTokenForUserId(token);
    if (verifyError) {
      return null;
    }
    return token;
  }
  return null;
};

interface GetBrowserUserOptions {
  existingClient: SupabaseClient<Database, "public">
}

export const getBrowserUser = async (
  nextReqRes: GetBrowserUser,
  opts?: GetBrowserUserOptions
): Promise<User | null> => {
  if (isBrowser()) {
    throw new Error("Not allowed");
  }
  const supabase = !opts?.existingClient ? createSupabaseAdminServerClient() : opts?.existingClient;
  const userJWT = await getBrowserJWT(nextReqRes);
  if (!userJWT) {
    return null;
  }
  const user = await supabase.auth.getUser(userJWT);
  if (!user.data.user?.id) {
    return null;
  }
  return user.data.user;
};